import { isBackwardCompatibleImgTiledSheet } from "@/components/r3f/renderers/sheet-renderer";
import { useCached3DObjectsIfReady } from "@/object-cache";
import { useAppSelector } from "@/store/store-hooks";
import {
  computeSheetSizeAndHeightAdjustment,
  getSheetSize,
} from "@faro-lotv/app-component-toolbox";
import {
  IElementGenericImgSheet,
  isIElementImgSheetTiled,
} from "@faro-lotv/ielement-types";
import { LodFloorPlan, memberWithPrivateData } from "@faro-lotv/lotv";
import { getElementTransformation } from "@faro-lotv/project-source";
import { useMemo } from "react";
import { Box3, Matrix4, Matrix4Tuple, Vector3, Vector3Tuple } from "three";

/**
 * Get the 4 corners of one sheet.
 *
 * @param sheet to compute the corners for
 * @param worldMatrixTuple the conversion matrix from sheet to world coordinates system
 * @returns the 4 corners for the sheet in world coordinates (including any rotation of the sheet)
 */
export function getSheetCorner(
  sheet: IElementGenericImgSheet,
  worldMatrixTuple: Matrix4Tuple,
): Vector3Tuple[] {
  const { width, height } = getSheetSize(sheet);

  const worldMatrix = new Matrix4().fromArray(worldMatrixTuple);

  return [
    new Vector3(0, 0, 0),
    new Vector3(width, 0, 0),
    new Vector3(0, height, 0),
    new Vector3(width, height, 0),
  ].map((v) => v.applyMatrix4(worldMatrix).toArray());
}

/**
 * Computes the world-space corner positions of a tiled sheet based on its dimensions and transformation matrices.
 *
 * @param sheet - The level of detail of a backward compatible image sheet tiled for which the size and Y adjustment should be computed.
 * A backward compatible tiled sheet contains information of a low-resolution image sheet in its metadata for compatibility with
 * older versions.
 * @param worldMatrixTuple - A 16-element tuple representing the world transformation matrix.
 * @returns An array of four 3D vectors (as tuples), each representing a corner of the sheet in world coordinates.
 */
export function getBackwardCompatibleTiledSheetCorners(
  sheet: LodFloorPlan,
  worldMatrixTuple: Matrix4Tuple,
): Vector3Tuple[] {
  const { sheetWidth, sheetHeight, heightAdjustmentInPercentage } =
    computeSheetSizeAndHeightAdjustment(sheet, true);

  const translation = new Matrix4().makeTranslation(
    0,
    sheetHeight * (1 - heightAdjustmentInPercentage),
    0,
  );
  const worldMatrix = new Matrix4().fromArray(worldMatrixTuple);

  return [
    new Vector3(0, 0, 0),
    new Vector3(sheetWidth, 0, 0),
    new Vector3(0, sheetHeight, 0),
    new Vector3(sheetWidth, sheetHeight, 0),
  ].map((v) => v.applyMatrix4(translation).applyMatrix4(worldMatrix).toArray());
}

/**
 * @param sheets list of sheets to compute the corners for
 * @returns the list of 4 interesting corners for each sheet
 */
export function useSheetCorners(
  sheets: IElementGenericImgSheet[],
): Vector3Tuple[] {
  const elementsState = useAppSelector((state) => state.iElements);
  const cachedSheets = useCached3DObjectsIfReady(sheets, "SheetCorners");

  return useMemo(() => {
    if (!sheets.length) return [];

    return sheets.reduce((corners: Vector3Tuple[], sheet) => {
      const { worldMatrix } = getElementTransformation(elementsState, sheet.id);
      if (
        isIElementImgSheetTiled(sheet) &&
        isBackwardCompatibleImgTiledSheet(sheet)
      ) {
        // find the object for this element (might not be ready yet)
        const sheetObject = cachedSheets.find(
          (sheetObj) => sheetObj.iElement.id === sheet.id,
        );
        if (sheetObject && sheetObject instanceof LodFloorPlan) {
          const sheetCorners = getBackwardCompatibleTiledSheetCorners(
            sheetObject,
            worldMatrix,
          );
          corners.push(...sheetCorners);
        }
      } else {
        corners.push(...getSheetCorner(sheet, worldMatrix));
      }
      return corners;
    }, []);
  }, [elementsState, sheets, cachedSheets]);
}

/**
 *
 * @param sheetCorners The four corners of a sheet
 * @param box The bounding box to add the corners to
 * @returns The argument box with the added corners
 */
export const sheetCorners2Box = memberWithPrivateData(() => {
  const point = new Vector3();

  return (sheetCorners: Vector3Tuple[], box = new Box3()): Box3 => {
    sheetCorners.forEach((p) => box.expandByPoint(point.set(p[0], p[1], p[2])));
    return box;
  };
});
